import { useCallback, useEffect, useRef, useState, useTransition } from "react";

import type { StatusWindow } from "./app-routing";
import { InlineSpinner, PendingNote, useSettledPending } from "./loading";

/* Per-service uptime history, in the shape a status page is actually read in:
 * a banner that answers "is anything wrong right now", then one row per service
 * with a bar you can scan for when it was wrong.
 *
 * The one deliberate departure from the familiar design: absence is its own
 * colour. Most of this estate is unobserved, and every public status page
 * renders "no data" the same grey as "fine" or omits the row entirely — which
 * is how a page comes to be trusted for something it never measured. A grey
 * bucket here means nothing was measured, and the figure beside the bar says
 * how many buckets the percentage is out of.
 */

export type BucketState = "ok" | "warn" | "bad" | "nodata";

export interface UptimeBucket {
  start_ms: number;
  total: number;
  errors: number;
  state: BucketState;
  /** Measured replica health, independent of request counts. */
  availability?: "ok" | "bad";
}

export interface ServiceUptime {
  service: string;
  /** Latest completed five-minute measurement, independent of history. */
  current?: UptimeBucket;
  /** The workload the bars were counted from, when it is not named after the
   *  service — `ai-gateway` runs as `ai-gateway-test` in test. */
  workload?: string;
  /** `traces` where requests were counted from spans, `logs` where they were
   *  read off the service's HTTP access log instead, `none` where a repository
   *  declares the service and nothing reported it. */
  source?: "traces" | "logs" | "metrics" | "none";
  /** The repository whose deployment/services/*.yaml declares this service. */
  repository?: string;
  tier?: number;
  owner?: string;
  runbook?: string;
  /** `prod`, `test`, `staging`, `dev`, or `unknown` where the service declares
   *  nothing. Never inferred from the name. */
  environment?: string;
  buckets: UptimeBucket[];
  healthy_percent: number | null;
  observed_buckets: number;
  /** Nothing has been measured for this row *yet* — it came from the service
   *  catalog while the span query is still running, or it is last window's row
   *  held in place while the new window loads.
   *
   *  Deliberately not the same thing as `source: "none"`, which is a finished
   *  answer: we looked, and the service was silent. Drawing an unfinished row
   *  as a silent one is the page telling you a service is dead while it is
   *  still being asked. */
  pending?: boolean;
}

export interface UptimeResponse {
  window: StatusWindow;
  bucket_seconds: number;
  buckets: number;
  generated_at_ms: number;
  /** Start of the newest bucket — the last complete step, not "now". */
  newest_bucket_ms?: number;
  services: ServiceUptime[];
  /** Reporting telemetry, but declared by no configured repository. */
  unclaimed_services?: string[];
  note?: string;
  /** How old the measurement behind this payload is. The endpoint serves a
   *  snapshot rebuilt on a timer, so this is never assumed to be zero. */
  age_seconds?: number;
  /** Under the endpoint's fresh window; shown without remark. */
  fresh?: boolean;
  /** Past the point where this should be read as the current state. */
  stale?: boolean;
  max_stale_seconds?: number;
  /** Set when a refresh failed and the previous snapshot is being served. */
  refresh_error?: { error?: string };
}

/** How the snapshot's age reads to someone deciding whether to trust the page.
 *
 *  A status page is the one place where showing a cached answer silently is a
 *  real hazard: a green banner built before an incident started is worse than
 *  no banner at all. So the age is always available, and past the endpoint's
 *  stale threshold the page says so rather than letting the bars speak.
 */
export function snapshotAge(seconds: number | undefined): string {
  if (seconds === undefined) return "";
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}

const WINDOW_LABEL: Record<StatusWindow, string> = {
  "30d": "Last 30 days, one bar per day",
  "24h": "Last 24 hours, one bar per half hour",
};

/** How many bars each window draws, mirroring the endpoint's own windows.
 *
 *  Only used to size the placeholder while a window loads, so the row does not
 *  jump when the real bars arrive.
 */
export const WINDOW_BUCKETS: Record<StatusWindow, number> = { "30d": 30, "24h": 48 };

/** What the banner says, from the worst thing on the page.
 *
 *  "All systems operational" is only claimed when something was actually
 *  observed. A page with nothing but grey says so instead, because the
 *  alternative is announcing health for services that were never measured.
 */
export function overallStatus(services: ServiceUptime[]): { tone: BucketState; message: string } {
  const recent = (service: ServiceUptime) => service.current ?? service.buckets[service.buckets.length - 1];
  const observed = services.filter((service) => service.current
    ? service.current.state !== "nodata" : service.observed_buckets > 0);
  if (services.length === 0) return { tone: "nodata", message: "No services are reporting" };
  if (observed.length === 0) {
    return { tone: "nodata", message: services.some(service => service.current)
      ? "Nothing observed in the latest five minutes" : "Nothing observed in this window" };
  }
  if (observed.some((service) => recent(service)?.availability === "bad")) {
    return { tone: "bad", message: "A service has fewer available replicas than desired" };
  }
  if (observed.some((service) => recent(service)?.state === "bad")) {
    return { tone: "bad", message: "A service is failing beyond its error budget" };
  }
  if (observed.some((service) => recent(service)?.state === "warn")) {
    return { tone: "warn", message: "Errors within budget on at least one service" };
  }
  // "Operational" is a claim about now -- the most recent bucket -- and a
  // service that failed four hours ago and recovered is operational. Said on a
  // page showing its red bar, though, it reads as the page contradicting
  // itself, so the banner counts what the bars are showing.
  const failedEarlier = observed.filter((service) =>
    service.buckets.some((bucket) => bucket.state === "bad" || bucket.state === "warn"),
  ).length;
  const earlier =
    failedEarlier > 0
      ? `; ${failedEarlier} had failures earlier in this window`
      : "";
  return {
    tone: "ok",
    message: `All observed systems operational (${observed.length} of ${services.length} measured)${earlier}`,
  };
}

/** The one thing a group's heading should say beyond its size.
 *
 *  A repository with forty-six rows hides its own summary: the failing row is
 *  at the top, but by the time a reader has scrolled past the healthy ones they
 *  have lost it. The heading carries the worst state in the group, in the same
 *  words as the legend.
 */
export function groupSummary(services: ServiceUptime[], counting = false): string | null {
  const has = (state: BucketState) =>
    services.filter((service) => service.current?.state === state || service.buckets.some((bucket) => bucket.state === state)).length;
  const bad = has("bad");
  if (bad > 0) return services.some(service => service.current?.availability === "bad" || service.buckets.some(bucket => bucket.availability === "bad"))
    ? `${bad} unhealthy` : `${bad} over budget`;
  const warn = has("warn");
  if (warn > 0) return `${warn} inside the budget`;
  // Silence is not a verdict until the access-log pass has answered: while it
  // runs, every service counted from logs is momentarily "not reporting", and a
  // heading that says so in red is wrong for the four seconds that takes -- on
  // exactly the repositories whose services are counted that way.
  if (counting) return null;
  const silent = services.filter((service) => service.observed_buckets === 0).length;
  if (silent > 0 && silent === services.length) return "none reporting";
  if (silent > 0) return `${silent} not reporting`;
  return null;
}

/** Human label for one bucket, used as the hover title. */
export function describeBucket(bucket: UptimeBucket, bucketSeconds: number): string {
  const start = new Date(bucket.start_ms);
  const stamp =
    bucketSeconds >= 86_400
      ? start.toISOString().slice(0, 10)
      : start.toISOString().slice(0, 16).replace("T", " ") + " UTC";
  if (bucket.state === "nodata") return `${stamp} — not observed`;
  const availability = bucket.availability
    ? `replica availability ${bucket.availability === "ok" ? "healthy" : "below desired"}`
    : "";
  if (bucket.total === 0 && availability) return `${stamp} — ${availability}; no request traffic measured`;
  const rate = bucket.total === 0 ? 0 : (bucket.errors / bucket.total) * 100;
  return `${stamp} — ${bucket.total.toLocaleString()} requests, ${rate.toFixed(2)}% failed${availability ? `; ${availability}` : ""}`;
}

/** Left-hand label for the time axis: how far back the oldest bucket sits.
 *
 *  Without it the bar is undirected — a reader cannot tell whether the newest
 *  bucket is on the left or the right, which is the one thing they need to
 *  know to read an outage off it.
 */
export function windowSpanLabel(bucketSeconds: number, buckets: number): string {
  const seconds = bucketSeconds * buckets;
  // The unit follows the span, not the bucket, with one caveat: a 24-hour
  // window is also exactly one day, and "1 day ago" over half-hour bars reads
  // as the wrong chart. Anything two days or under is said in hours.
  if (seconds <= 172_800) {
    const hours = Math.round(seconds / 3_600);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.round(seconds / 86_400);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** Right-hand axis label: the newest bucket the data actually covers.
 *
 *  Not "now". SigNoz does not report the step in progress, so the newest bar is
 *  the last complete one — up to a day old on the 30-day view. Labelling it
 *  "now" would date an outage wrong by a whole bar.
 */
export function newestLabel(data: UptimeResponse): string {
  const newest = data.newest_bucket_ms;
  if (newest === undefined) return "latest";
  // Labelled by the bucket's own start, exactly as the bars are: a day bucket
  // named by the midnight that ends it names the wrong day.
  const start = new Date(newest);
  return data.bucket_seconds >= 86_400
    ? start.toISOString().slice(0, 10)
    : `${start.toISOString().slice(11, 16)} UTC`;
}

export interface CoverageResponse {
  window: StatusWindow;
  /** Rows for services that serve requests but emit no spans. */
  services: ServiceUptime[];
  /** Instrumented services whose spans carry no countable requests. */
  uncountable: string[];
  /** Workloads that only write logs and log nothing request-shaped. */
  log_only_workloads: number;
  age_seconds?: number;
  stale?: boolean;
}

/** The rows to draw: the fast ones, then any the deferred pass added.
 *
 *  Services that serve traffic without emitting spans — anything behind nginx —
 *  can only be counted from their access logs, and that query is several times
 *  slower than the span one. They arrive second and are appended rather than
 *  re-sorted in, so nothing already on screen moves under the reader.
 */
export function mergedServices(
  data: UptimeResponse | null,
  coverage: CoverageResponse | null,
): ServiceUptime[] {
  if (!data) return [];
  if (!coverage || coverage.window !== data.window) return data.services;
  const key = (service: ServiceUptime) =>
    `${service.service}:${service.environment ?? "unknown"}`;
  const arrived = new Map(coverage.services.map((service) => [key(service), service] as const));
  // A row the fast pass could only declare ("no signal") is replaced by the
  // deferred one that actually counted it: the dashboard emits no spans, so its
  // bars exist only in the access-log pass. Skipping it would leave a service
  // with real traffic drawn as silent.
  const merged = data.services.map((service) => {
    const logged = arrived.get(key(service));
    if (!logged) return service;
    if (service.buckets.every(bucket => bucket.state === "nodata")) return { ...logged, current: service.current };
    // Preserve retained trace days while access logs fill unobserved periods.
    const logs = new Map(logged.buckets.map(bucket => [bucket.start_ms, bucket]));
    const buckets = service.buckets.map(bucket => {
      const log = logs.get(bucket.start_ms);
      const selected = service.source === "traces" && bucket.total > 0 && bucket.state !== "nodata"
        ? bucket : log && log.state !== "nodata" ? log : bucket;
      if (!bucket.availability) return selected;
      return { ...selected, availability: bucket.availability,
        state: bucket.availability === "bad" ? "bad" as const
          : selected.state === "nodata" ? "ok" as const : selected.state };
    });
    const observed = buckets.filter(bucket => bucket.state !== "nodata");
    const healthy = observed.filter(bucket => bucket.state === "ok");
    return { ...service, buckets, source: (service.source === "metrics" || service.source === "none")
      && logged.source === "logs" && buckets.some(bucket => bucket.total > 0) ? "logs" : service.source,
      observed_buckets: observed.length,
      healthy_percent: observed.length ? healthy.length / observed.length * 100 : null };
  });
  const shown = new Set(merged.map(key));
  return [...merged, ...coverage.services.filter((service) => !shown.has(key(service)))];
}

/* ---------------------------------------------------------------------------
 * Stage 0: the service catalog.
 *
 * The page used to open on four blank grey rows and sit there for a second or
 * more, and on a window switch it threw the environment tabs away entirely —
 * so the one control a reader reaches for while waiting ("just show me prod")
 * vanished exactly when they reached for it.
 *
 * The catalog is the estate as the repositories declare it, and it costs no
 * SigNoz query at all. Asking for it alongside the spans lets the real service
 * names, their environments and the environment tabs land immediately, with the
 * bars filling in behind them.
 * ------------------------------------------------------------------------- */

export interface CatalogEntry {
  repository: string;
  /** Internally tagged by `state`: `defined` carries the service definition's
   *  fields inline, the other states carry only a reason. */
  outcome: {
    state: string;
    name?: string;
    tier?: number;
    owner?: string;
    runbook?: string;
    /** Environment name as the definition spells it, to namespace. */
    namespaces?: Record<string, string>;
    /** Every environment the definition declares, namespace or not. A compose
     *  service has no namespace and would otherwise read as `unknown`. */
    environments?: string[];
  };
}

export interface CatalogResponse {
  repositories: number;
  defined: number;
  entries: CatalogEntry[];
}

/** Which environment a declared namespace belongs to.
 *
 *  A mirror of `normalised_environment` in `uptime.rs`, and it has to stay one:
 *  a row placed under `prod-env` here and `prod` there is the same service
 *  drawn twice, once by each stage. The collectors on three clusters spell the
 *  same four environments six ways, so the word inside the value is what is
 *  read — and anything that says none of them stays `unknown` rather than being
 *  guessed onto a production page.
 */
export function normalisedEnvironment(raw: string): string {
  const value = raw.toLowerCase();
  if (value.includes("stag")) return "staging";
  if (value.includes("test") || value.includes("qa")) return "test";
  if (value.includes("dev")) return "dev";
  if (value.includes("prod")) return "prod";
  return "unknown";
}

/** Where a definition's rows belong, from the environments it declares.
 *
 *  A mirror of `declared_environments` in `uptime.rs`, and it has to stay one:
 *  a row this stage places under `test` and the uptime endpoint places under
 *  `prod` is the same service drawn twice, once by each stage.
 *
 *  A definition that names exactly one environment is naming the only place
 *  that service runs, so that is its production whatever the namespace is
 *  called — ScraperX spells its sole environment `test` for all sixteen
 *  services and has nothing behind it. Two or more are read as declared: a
 *  service carrying both has already said which is which.
 */
export function declaredEnvironments(declared: string[]): string[] {
  const environments = [...new Set(declared.map(normalisedEnvironment))].sort();
  return environments.length === 1 ? ["prod"] : environments;
}

/** Catalog entries as rows to draw while the measurement is still running.
 *
 *  One row per environment the definition declares, matching what the uptime
 *  endpoint will itself produce — photogenix declares test and prod, and a
 *  single row would have it vanish from one of the two views the moment the
 *  real data replaced it.
 *
 *  Every row is `pending`: the catalog says the service exists, and nothing
 *  more. It carries no bars and makes no claim about health.
 */
export function catalogRows(catalog: CatalogResponse | null): ServiceUptime[] {
  if (!catalog) return [];
  const rows: ServiceUptime[] = [];
  for (const entry of catalog.entries) {
    const { outcome } = entry;
    if (outcome.state !== "defined" || !outcome.name) continue;
    const environments = declaredEnvironments(
      outcome.environments ?? Object.keys(outcome.namespaces ?? {}),
    );
    if (environments.length === 0) environments.push("unknown");
    for (const environment of environments) {
      rows.push({
        service: outcome.name,
        repository: entry.repository,
        tier: outcome.tier,
        owner: outcome.owner,
        runbook: outcome.runbook,
        environment,
        buckets: [],
        healthy_percent: null,
        observed_buckets: 0,
        pending: true,
      });
    }
  }
  return rows;
}

/** Strip a row back to its identity, keeping its place while it is re-measured.
 *
 *  Used on a window switch. The name and environment belong to the service and
 *  stay true across windows; the bars and the percentage belong to the window
 *  being left, and thirty daily bars standing under a "24 hours" tab are a
 *  stale claim, not a slow one.
 */
export function asPending(service: ServiceUptime): ServiceUptime {
  return {
    ...service,
    current: undefined,
    buckets: [],
    healthy_percent: null,
    observed_buckets: 0,
    pending: true,
  };
}

/** The rows to draw right now, from whichever stages have landed.
 *
 *  Measured rows win outright. Failing those, the rows already on screen are
 *  held in place (re-measuring, not re-discovering), and failing those the
 *  catalog draws the estate. The point of the ladder is that the row list and
 *  the environment tabs never empty out mid-load: a page that collapses to
 *  nothing and springs back is unreadable, and un-clickable, precisely while
 *  someone is waiting on it.
 */
export function progressiveRows(
  data: UptimeResponse | null,
  coverage: CoverageResponse | null,
  catalog: CatalogResponse | null,
  previous: ServiceUptime[],
): ServiceUptime[] {
  const measured = mergedServices(data, coverage);
  if (measured.length > 0) return measured;
  if (previous.length > 0) return previous.map(asPending);
  return catalogRows(catalog);
}

/** The figure printed beside a row's bars, and whether it is still settling.
 *
 *  The ordering matters more than it looks. "declared, no signal" is a verdict
 *  reached only after *both* passes — the span query said nothing and the
 *  access-log query said nothing either. Printed while the log query is still
 *  running it is simply wrong, and it was wrong for the four seconds that pass
 *  takes, on exactly the services most likely to be counted from logs.
 */
export function figureLabel(
  service: ServiceUptime,
  counting: boolean,
): { pending: boolean; text: string } {
  if (service.pending) return { pending: true, text: "measuring" };
  if (service.source === "none") {
    return counting
      ? { pending: true, text: "counting from logs" }
      : { pending: false, text: "declared, no signal" };
  }
  if (service.healthy_percent === null) return { pending: false, text: "never observed" };
  // The denominator is printed on purpose: "100%" over two observed buckets and
  // over thirty are different claims, and a bare percentage hides which one you
  // are reading.
  return {
    pending: false,
    text: `${service.healthy_percent.toFixed(0)}% of ${service.observed_buckets} observed`,
  };
}

/** What the environment filter should say about itself, if anything.
 *
 *  Three different waits can sit behind that row of tabs, and they are not
 *  interchangeable: the filter re-rendering, the bars not being there yet, and
 *  the slow pass that is still adding rows to the very environment you picked.
 *  Saying "loading" to all three would be true and useless — the reader wants
 *  to know whether to wait for more rows, and only the third one means yes.
 *
 *  `null` means say nothing, and is the honest answer once everything has
 *  landed: a filter that completes in a frame does not get a spinner.
 */
export function environmentBusyLabel(state: {
  switching: boolean;
  measuring: boolean;
  counting: boolean;
  environment: string;
}): string | null {
  const where = state.environment === ALL_ENVIRONMENTS ? "all environments" : state.environment;
  if (state.measuring) return `Measuring ${where}…`;
  if (state.switching) return `Filtering to ${where}…`;
  if (state.counting) return "Still counting services that emit no spans — more rows may arrive";
  return null;
}

/** One repository's rows, in the order the server sent them. */
export interface ServiceGroup {
  /** `owner/name`, or null for a row no repository claims. */
  repository: string | null;
  /** What the heading shows: the repository without its owner. */
  label: string;
  services: ServiceUptime[];
}

/** The repository name as a heading: `catalogix`, not
 *  `Streamoid-Technologies/catalogix`. Every repository here has the same
 *  owner, so printing it 9 times spends the widest column on the least
 *  informative half of the string.
 */
export function repositoryLabel(repository: string | null | undefined): string {
  // Short, and deliberately not "declared by no repository": that exact phrase
  // already heads the section listing workloads the page leaves off, and two
  // headings reading the same would say the rows are the same rows.
  if (!repository) return "unclaimed";
  const [, name] = repository.split("/");
  return name || repository;
}

/** Rows grouped by the repository that declares them.
 *
 *  Alphabetical throughout: the repositories, and the rows inside each of them.
 *  A page someone returns to is a page they navigate by memory, and both orders
 *  it could have instead — the server's failing-first, or first-appearance —
 *  move a row between visits, so finding `products-set` means hunting for it
 *  twice.
 *
 *  Nothing is lost by dropping health-order: the banner says what failed in the
 *  window, each heading carries its own repository's worst state, and a failing
 *  row is red across its whole width. None of those are found by position.
 *
 *  A service's environments sort together and prod leads, because `prod`
 *  precedes `test`. The rows no repository claims sort last whatever they are
 *  called: "unclaimed" is not a repository name, and putting it in the U's
 *  would say it was one.
 */
export function groupByRepository(services: ServiceUptime[]): ServiceGroup[] {
  const groups = new Map<string, ServiceGroup>();
  for (const service of services) {
    const key = service.repository ?? "";
    const group = groups.get(key);
    if (group) {
      group.services.push(service);
      continue;
    }
    groups.set(key, {
      repository: service.repository ?? null,
      label: repositoryLabel(service.repository),
      services: [service],
    });
  }
  const byName = (left: ServiceUptime, right: ServiceUptime) =>
    left.service.localeCompare(right.service) ||
    (left.environment ?? "unknown").localeCompare(right.environment ?? "unknown");
  return [...groups.values()]
    .map((group) => ({ ...group, services: [...group.services].sort(byName) }))
    .sort(
      (left, right) =>
        Number(left.repository === null) - Number(right.repository === null) ||
        left.label.localeCompare(right.label),
    );
}

/** How many rows sit in each environment, most populated first.
 *
 *  Shown next to the filter even while one environment is selected: a status
 *  page that quietly drops two thirds of the estate is worse than one that
 *  mixes it, so the counts say what is being left out.
 */
export function environmentCounts(services: ServiceUptime[]): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const service of services) {
    const environment = service.environment ?? "unknown";
    counts.set(environment, (counts.get(environment) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** The environment to open on.
 *
 *  Production, when anything reports it. The page is read to find out whether
 *  customers are seeing failures, and on this estate the noisiest rows by far
 *  are test workers retrying all day — they were sitting above the customer
 *  facing services. Where nothing declares an environment there is nothing to
 *  prefer, so everything is shown.
 */
export function defaultEnvironment(services: ServiceUptime[]): string {
  return services.some((service) => service.environment === "prod") ? "prod" : ALL_ENVIRONMENTS;
}

export const ALL_ENVIRONMENTS = "all";

export function StatusPage({
  statusWindow,
  onWindowChange,
  initialData = null,
}: {
  statusWindow: StatusWindow;
  onWindowChange: (next: StatusWindow) => void;
  /** Rendered immediately instead of the placeholder. For tests, and for a
   *  caller that already holds a snapshot. */
  initialData?: UptimeResponse | null;
}) {
  const [data, setData] = useState<UptimeResponse | null>(initialData);
  const [coverage, setCoverage] = useState<CoverageResponse | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  // Tracked apart from `coverage === null`, which cannot tell "not asked yet"
  // from "asked, and it had nothing to add" — and only the first of those is
  // worth a spinner.
  const [counting, setCounting] = useState(false);
  const [environment, setEnvironment] = useState<string>(ALL_ENVIRONMENTS);
  // The filter runs as a transition so React can keep the tabs responsive while
  // it re-renders the rows, and so `switching` is the real cost of the switch
  // rather than a timer we made up. On a handful of rows it is over within a
  // frame and nothing is shown; on the full estate at thirty bars a row it is
  // not, and then it is.
  const [switching, startSwitch] = useTransition();
  // Chosen from the data rather than assumed, and only until the reader picks:
  // a window with no production row would otherwise open on an empty page.
  const [environmentPicked, setEnvironmentPicked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(initialData === null);
  // A forced refresh is not a load: the bars stay on screen and only the
  // control shows the work, because clearing a status page to a skeleton to
  // re-fetch what is already correct is a worse answer than a stale second.
  const [refreshing, setRefreshing] = useState(false);
  // When the snapshot on screen was received, so its age can go on ticking
  // between fetches rather than freezing at whatever the endpoint last said.
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Consumed by the coverage effect on the pass it triggers. The effect keys on
  // `data` alone and deliberately so (see below), so the intent to force has to
  // travel outside the dependency list.
  const forceCoverage = useRef(false);

  const load = useCallback(async (window: StatusWindow, force = false) => {
    if (!force) setLoading(true);
    setError(null);
    // The previous window's bars are cleared rather than left dimmed: thirty
    // daily bars standing under a "24 hours" tab are a different claim, and a
    // stale one.
    if (!force) {
      setData((previous) => (previous && previous.window === window ? previous : null));
      setCoverage(null);
      setCounting(false);
    }
    try {
      const response = await fetch(
        `/api/portal/v1/status/uptime?window=${window}${force ? "&refresh=1" : ""}`,
        { credentials: "include" },
      );
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        // The endpoint reports a refused upstream query and a missing
        // credential distinctly, and both are worth showing verbatim: an empty
        // page and a broken query look identical otherwise.
        setError(body?.error ?? `Could not load uptime (${response.status})`);
        // A refresh that fails leaves the measurement that was already on
        // screen: it is old, and the age beside it now says so, but it is a
        // real measurement and an empty page is not.
        if (!force) setData(null);
        return;
      }
      setData(body as UptimeResponse);
      setReceivedAt(Date.now());
      setNowMs(Date.now());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load uptime");
      if (!force) setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /** Rebuild the snapshot upstream rather than re-reading the cached one. */
  const refresh = useCallback(() => {
    setRefreshing(true);
    forceCoverage.current = true;
    void load(statusWindow, true);
  }, [load, statusWindow]);

  // The age on screen has to keep moving on its own. Ten seconds is under the
  // resolution the label is written in, so it never shows a number that has
  // already rolled over.
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    void load(statusWindow);
  }, [load, statusWindow]);

  // Opened on the best stage that has landed, not on the measured one.
  //
  // The catalog arrives first and already knows which services declare a
  // production environment, so the page can open on `prod` straight away. Left
  // waiting for the bars it would open on `all`, show the whole estate for a
  // second, then jump to `prod` under the reader — the two stages agree on the
  // answer, so the only thing waiting bought was the jump.
  useEffect(() => {
    if (environmentPicked) return;
    const known = data ? data.services : catalogRows(catalog);
    if (known.length === 0) return;
    setEnvironment(defaultEnvironment(known));
  }, [data, catalog, environmentPicked]);

  // Stage 0, in parallel with the bars and never gating them. The catalog is
  // read from GitHub and cached, so it lands well before either SigNoz query —
  // which is the whole point: names and environments on screen while the
  // measurement is still running. It is window-independent, so it is asked for
  // once and kept across a window switch.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const response = await fetch("/api/portal/v1/status/catalog", {
          credentials: "include",
        });
        if (!response.ok) return;
        const body = (await response.json()) as CatalogResponse;
        if (active) setCatalog(body);
      } catch {
        /* Purely a head start. Without it the page loads exactly as it did
         * before, so an unreadable catalog is not worth an error banner. */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Stage 2. Runs *after* the page has rendered and never blocks it: measured
  // live at ~4.4s against ~1.2s for the page's own query, because it has to look
  // at every span kind. If it fails there is nothing to show and nothing to
  // report — the page it annotates is already correct.
  // Keyed to the data it annotates, and to nothing else. Keying it to
  // `statusWindow` looked equivalent and was not: `load` clears the data from
  // inside an async function, so on a window switch this effect re-ran in the
  // same commit and still saw the *outgoing* window's data — firing a coverage
  // query for a window whose bars had not arrived, and announcing a pass that
  // had not started. Depending on `data` alone, the switch simply tears the
  // effect down until the new window's bars land.
  useEffect(() => {
    if (!data) return;
    const window = data.window;
    let active = true;
    const force = forceCoverage.current;
    forceCoverage.current = false;
    setCounting(true);
    void (async () => {
      try {
        const response = await fetch(
          `/api/portal/v1/status/coverage?window=${window}${force ? "&refresh=1" : ""}`,
          { credentials: "include" },
        );
        if (!response.ok) return;
        const body = (await response.json()) as CoverageResponse;
        if (active && body.window === window) setCoverage(body);
      } catch {
        /* The bars stand on their own; this only ever adds a footnote. */
      } finally {
        if (active) setCounting(false);
      }
    })();
    return () => {
      active = false;
      // The in-flight pass can no longer report, so nothing is still counting.
      setCounting(false);
    };
  }, [data]);

  // Keep the rows that were on screen so the page does not collapse and jump
  // while a window loads — identity only, never their measurements.
  const [knownRows, setKnownRows] = useState<ServiceUptime[]>([]);
  useEffect(() => {
    if (data) setKnownRows(mergedServices(data, coverage));
  }, [data, coverage]);

  const allRows = progressiveRows(loading ? null : data, coverage, catalog, knownRows);
  const environments = environmentCounts(allRows);
  const rows =
    environment === ALL_ENVIRONMENTS
      ? allRows
      : allRows.filter((service) => (service.environment ?? "unknown") === environment);
  // Rows drawn before anything measured them. Even the catalog can be missing
  // on a cold page, and then there is nothing to name — four blanks keep the
  // list the right height rather than letting the page snap open.
  const blanks = allRows.length === 0 && loading ? [0, 1, 2, 3] : [];

  // The banner describes what is on screen: with a filter applied, a claim made
  // from the whole estate would be about rows the reader cannot see.
  const overall = data && !loading ? overallStatus(rows) : null;
  const groups = groupByRepository(rows);
  const uncountable = loading ? [] : (coverage?.uncountable ?? []);
  const unclaimed = loading ? [] : (data?.unclaimed_services ?? []);
  const logOnly = loading ? 0 : (coverage?.log_only_workloads ?? 0);

  // The age of the measurement, counted on from when this page received it.
  // The endpoint's own figure is the age at the moment it answered, and a page
  // left open for ten minutes must not keep claiming the number it was handed.
  const snapshotSeconds =
    data?.age_seconds === undefined
      ? undefined
      : data.age_seconds + Math.max(0, Math.floor((nowMs - receivedAt) / 1000));
  const maxStale = data?.max_stale_seconds ?? 300;
  const tooOld = snapshotSeconds !== undefined && snapshotSeconds >= maxStale;
  const refreshFailed = data?.refresh_error?.error;

  // A spinner is only worth showing for work the reader can notice; see
  // `useSettledPending`. Without this the tab flickers on every click.
  const filtering = useSettledPending(switching);
  const busy = environmentBusyLabel({
    switching: filtering,
    measuring: loading,
    counting,
    environment,
  });
  const pickEnvironment = (name: string) => {
    setEnvironmentPicked(true);
    startSwitch(() => setEnvironment(name));
  };

  return (
    <div className="status-page">
      <header className="status-head">
        <div>
          <h1>Service status</h1>
          <p className="status-sub">{WINDOW_LABEL[statusWindow]}</p>
        </div>
        <div className="status-windows" role="tablist" aria-label="Time window">
          {(["30d", "24h"] as StatusWindow[]).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={statusWindow === option}
              className={statusWindow === option ? "active" : ""}
              onClick={() => onWindowChange(option)}
            >
              {option === "30d" ? "30 days" : "24 hours"}
            </button>
          ))}
        </div>
        <div className="status-refresh">
          {snapshotSeconds !== undefined ? (
            <span
              className={`status-age${tooOld ? " stale" : ""}`}
              // The precise figure for anyone who needs it; the label rounds.
              title={`Measured ${snapshotSeconds}s ago`}
            >
              {refreshing ? "refreshing…" : `measured ${snapshotAge(snapshotSeconds)}`}
            </span>
          ) : null}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || loading}
            aria-busy={refreshing ? "true" : undefined}
            // Named for what it costs, because it is not a free redraw: it
            // rebuilds both SigNoz queries behind the page.
            title="Re-run the SigNoz queries behind this page"
          >
            {refreshing ? <InlineSpinner label="Refreshing" labelled={false} /> : null}
            Refresh
          </button>
        </div>
      </header>

      {/* Ahead of the banner, deliberately. Past this age the page cannot
          claim to describe the estate as it stands, and a reader who takes the
          green banner at face value is the failure this page exists to avoid. */}
      {tooOld && !refreshing ? (
        <div className="status-banner tone-warn" role="status">
          This measurement is {snapshotAge(snapshotSeconds)} and is no longer current. Refresh
          before acting on it.
        </div>
      ) : null}
      {refreshFailed ? (
        <div className="status-banner tone-warn" role="status">
          Could not refresh ({refreshFailed}). Showing the last measurement that succeeded.
        </div>
      ) : null}

      {overall ? (
        <div className={`status-banner tone-${overall.tone}`} role="status">
          {overall.message}
        </div>
      ) : null}

      {environments.length > 0 ? (
        <div className="status-environments" role="tablist" aria-label="Environment">
          {[[ALL_ENVIRONMENTS, allRows.length] as [string, number], ...environments].map(
            ([name, total]) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={environment === name}
                aria-busy={environment === name && busy !== null ? "true" : undefined}
                className={environment === name ? "active" : ""}
                onClick={() => pickEnvironment(name)}
              >
                {name === ALL_ENVIRONMENTS ? "all environments" : name} <b>{total}</b>
                {/* On the selected tab only. A spinner on every tab would say
                    four things are loading when one is. */}
                {environment === name && busy !== null ? (
                  <InlineSpinner label={busy} labelled={false} className="status-tab-spinner" />
                ) : null}
              </button>
            ),
          )}
          {busy !== null ? <span className="status-env-busy" role="status">{busy}</span> : null}
        </div>
      ) : null}

      {loading ? (
        <div className="status-banner tone-loading" role="status">
          <span className="status-spinner" aria-hidden="true" />
          Reading {statusWindow === "30d" ? "30 days" : "24 hours"} of spans from SigNoz…
        </div>
      ) : null}
      {error ? <div className="status-banner tone-bad">{error}</div> : null}

      <div className="status-legend">
        <span><i className="seg ok" /> no failures</span>
        <span><i className="seg warn" /> inside the error budget</span>
        <span><i className="seg bad" /> over budget</span>
        <span><i className="seg nodata" /> not observed</span>
      </div>

      {data?.services.length === 0 && !loading ? (
        <p className="status-empty">No service reported a span in this window.</p>
      ) : null}

      {/* Blank rows only while nothing at all has landed: until the catalog
          arrives there is no repository to group them under. */}
      {blanks.length > 0 ? (
        <ul className="status-rows" aria-busy="true">
          {blanks.map((index) => (
            <li key={`blank-${index}`} className="status-row">
              <div className="status-name">
                <span className="skeleton-text" aria-hidden="true" />
              </div>
              <div className="status-bar">
                {Array.from({ length: WINDOW_BUCKETS[statusWindow] }, (_, bucket) => (
                  <i key={bucket} className="seg pending" />
                ))}
              </div>
              <div className="status-figure">—</div>
            </li>
          ))}
        </ul>
      ) : null}

      {/* One section per repository. The rows are the same rows throughout —
          they gain their bars, then their figures — so nothing unmounts and
          remounts under the reader as each stage lands. */}
      {groups.map((group) => (
      <section className="status-group" key={group.repository ?? "unclaimed"}>
        <h2 className="status-group-head">
          {group.repository ? (
            <a href={`https://github.com/${group.repository}`} target="_blank" rel="noreferrer">
              {group.label}
            </a>
          ) : (
            group.label
          )}
          <span className="status-group-count">
            {group.services.length === 1 ? "1 service" : `${group.services.length} services`}
          </span>
          {(() => {
            const summary = groupSummary(group.services, counting);
            return summary ? <span className="status-group-summary">{summary}</span> : null;
          })()}
        </h2>
      <ul className="status-rows" aria-busy={loading ? "true" : undefined}>
        {group.services.map((service) => (
          // A service declared in two environments is two rows; keying on the
          // name alone would collapse them into one.
          <li key={`${service.service}:${service.environment ?? "unknown"}`} className="status-row">
            <div
              className="status-name"
              title={
                service.workload && service.workload !== service.service
                  ? `Counted from the workload ${service.workload}`
                  : undefined
              }
            >
              {service.runbook ? (
                <a href={service.runbook} target="_blank" rel="noreferrer">
                  {service.service}
                </a>
              ) : (
                service.service
              )}
              {(service.environment ?? "unknown") !== "prod" ? (
                <span className={`status-env env-${service.environment ?? "unknown"}`}>
                  {service.environment ?? "unknown"}
                </span>
              ) : null}
              {service.source === "logs" ? (
                <span className="status-source" title="Counted from this service's HTTP access log, because it emits no spans">
                  logs
                </span>
              ) : null}
              {service.buckets.some(bucket => bucket.availability) ? (
                <span className="status-source" title="Includes measured Kubernetes replica availability">
                  replica health
                </span>
              ) : null}
              {!service.pending && service.current ? (
                <span className={`status-source state-${tooOld ? "nodata" : service.current.state}`}
                  title={describeBucket(service.current, 300)}>
                  {tooOld ? "measurement stale" : service.current.state === "ok" ? "healthy now" : service.current.state === "bad"
                    ? "unhealthy now" : service.current.state === "warn" ? "errors now" : "not currently observed"}
                </span>
              ) : null}
            </div>
            <div className="status-bar" aria-label={`${service.service} uptime`}>
              {service.pending
                ? /* Sized to the window being loaded, not the one being left,
                     so the row keeps its shape when the real bars land. */
                  Array.from({ length: WINDOW_BUCKETS[statusWindow] }, (_, bucket) => (
                    <i key={bucket} className="seg pending" />
                  ))
                : service.buckets.map((bucket) => (
                    <i
                      key={bucket.start_ms}
                      className={`seg ${bucket.state}`}
                      title={describeBucket(bucket, data?.bucket_seconds ?? 0)}
                    />
                  ))}
            </div>
            <div className="status-figure">
              {(() => {
                const figure = figureLabel(service, counting);
                return figure.pending
                  ? <PendingNote>{figure.text}</PendingNote>
                  : figure.text;
              })()}
            </div>
          </li>
        ))}
      </ul>
      </section>
      ))}

      {data && !loading && rows.length > 0 ? (
        <p className="status-axis" aria-hidden="true">
          <span />
          <span className="status-axis-span">
            <span>{windowSpanLabel(data.bucket_seconds, data.buckets)}</span>
            <span>{newestLabel(data)}</span>
          </span>
          <span />
        </p>
      ) : null}

      {counting ? (
        <p className="status-note status-counting">
          <InlineSpinner label="Counting services that emit no spans" labelled={false} />
          Counting the services that emit no spans from their HTTP access logs. That query is
          several times slower than the span one, so it runs after the page rather than in front
          of it — rows and footnotes below may still arrive.
        </p>
      ) : null}

      {unclaimed.length > 0 ? (
        <div className="status-unmeasured">
          <h2>Reporting, but declared by no repository ({unclaimed.length})</h2>
          <p>
            These are serving traffic under a name that no configured repository claims in
            its <code>deployment/services/</code>. They are off the bars above because the
            page is the declared estate, and named here because something is still running
            them.
          </p>
          <ul>
            {unclaimed.map((service) => (
              <li key={service}>{service}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {uncountable.length > 0 || logOnly > 0 ? (
        <div className="status-unmeasured">
          <h2>Reporting, but with no request to count ({uncountable.length + logOnly})</h2>
          <p>
            Neither source could turn what these emit into a request rate. Listed rather
            than omitted: a service that vanishes from a status page reads as a healthy one.
          </p>
          {uncountable.length > 0 ? (
            <ul>
              {uncountable.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          ) : null}
          {logOnly > 0 ? (
            <p className="status-note">
              And {logOnly} workloads that write logs but nothing request-shaped — a service
              under bun, say, which cannot run the Node auto-instrumentation and logs no
              access line. Most of the rest is infrastructure. Telling ours apart from a
              backup agent needs the service catalog, not a guess, so they are counted here
              rather than named.
            </p>
          ) : null}
        </div>
      ) : null}

      {data?.note ? <p className="status-note">{data.note}</p> : null}
      {data ? (
        <p className="status-note">
          Generated {new Date(data.generated_at_ms).toISOString().slice(0, 16).replace("T", " ")} UTC
        </p>
      ) : null}
    </div>
  );
}
